import { apiService } from "@/api";
import { getUserInfoGlobal } from "@/api/config";
import PanelFooter from "@/coral/components/Panels/PanelFooter";
import PanelLeft from "@/coral/components/Panels/PanelLeft";
import PanelLeftToolbar from "@/coral/components/Panels/PanelLeftToolbar";
import { Session, Task, UserInfo } from "@/models";
import { NewTaskService } from "@/services";
import UserMenu from "@/components/UserMenu";
import {
    Body1Strong,
    Caption1,
    Tooltip,
} from "@fluentui/react-components";
import {
    Edit20Regular,
} from "@fluentui/react-icons";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import EricssonLogo from "../../coral/imports/EricssonLogo";
import "../../styles/PlanPanelLeft.css";
import TaskList from "./TaskList";

/**
 * Left panel for conversation page
 * Shows navigation, new task button, and conversation history
 */
const ConversationPanelLeft: React.FC = () => {
    const navigate = useNavigate();
    const { sessionId } = useParams<{ sessionId: string }>();

    const [historyTasks, setHistoryTasks] = useState<Task[]>([]);
    const [sessions, setSessions] = useState<Session[] | null>(null);
    const [sessionsLoading, setSessionsLoading] = useState<boolean>(false);
    const [userInfo, setUserInfo] = useState<UserInfo | null>(
        getUserInfoGlobal()
    );

    const loadSessionsData = useCallback(async (forceRefresh = false) => {
        try {
            setSessionsLoading(true);
            const sessionsData = await apiService.getConversationSessions(!forceRefresh);
            setSessions(sessionsData);
        } catch (error) {
            console.error("Failed to load sessions:", error);
        } finally {
            setSessionsLoading(false);
        }
    }, []);

    useEffect(() => {
        loadSessionsData();
    }, [loadSessionsData]);

    // Listen for session refresh events (triggered when first message response arrives)
    useEffect(() => {
        const cleanup = NewTaskService.addSessionsRefreshListener(() => {
            loadSessionsData(true);
        });
        return cleanup;
    }, [loadSessionsData]);

    useEffect(() => {
        if (sessions) {
            const historySessionTasks: Task[] = sessions
                .map(session => {
                    const sessionDate = new Date(session.timestamp);
                    const formattedDate = new Intl.DateTimeFormat(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    }).format(sessionDate);

                    return {
                        id: session.id,
                        name: session.message_to_user || `Conversation ${formattedDate}`,
                        status: 'history' as const,
                        date: new Intl.DateTimeFormat(undefined, {
                            dateStyle: 'medium',
                        }).format(sessionDate),
                    };
                });

            setHistoryTasks(historySessionTasks);
        }
    }, [sessions]);

    const selectedTaskId = sessionId ?? null;

    const handleTaskSelect = useCallback(
        (taskId: string) => {
            navigate(`/conversation/${taskId}`);
        },
        [navigate]
    );

    return (
        <div style={{ flexShrink: 0, display: "flex", overflow: "hidden" }}>
            <PanelLeft panelWidth={280} panelResize={true}>
                <PanelLeftToolbar
                    linkTo="/"
                    panelTitle="Ericsson"
                    panelIcon={<EricssonLogo />}
                >
                    <Tooltip content="New task" relationship={"label"} />
                </PanelLeftToolbar>

                <div
                    className="tab tab-new-task"
                    onClick={() => navigate("/", { state: { focusInput: true } })}
                    tabIndex={0}
                    role="button"
                    style={{ marginTop: '8px' }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            navigate("/", { state: { focusInput: true } });
                        }
                    }}
                >
                    <Edit20Regular className="tab-new-task-icon" />
                    <Body1Strong>New chat</Body1Strong>
                </div>

                <TaskList
                    historyTasks={historyTasks}
                    inProgressTasks={[]}
                    completedTasks={[]}
                    onTaskSelect={handleTaskSelect}
                    loading={sessionsLoading}
                    selectedTaskId={selectedTaskId ?? undefined}
                    showInProgress={false}
                    showCompleted={false}
                />

                <PanelFooter>
                    <UserMenu userInfo={userInfo} />
                    <Caption1
                        style={{
                            marginTop: '8px',
                            color: '#616161',
                            textAlign: 'center',
                            display: 'block'
                        }}
                    >
                        version: v0.3.0
                    </Caption1>
                </PanelFooter>
            </PanelLeft>
        </div>
    );
};

export default ConversationPanelLeft;
